<template>
  <div class="pc" :class="{ 'pc-editing': editing }">
    <div class="pc-bar">
      <div class="pc-filters">
        <label v-for="f in spec.filters || []" :key="f.name" class="pc-filter">
          <span>{{ f.label || f.name }}</span>
          <select v-if="f.type === 'choice'" v-model="values[f.name]">
            <option value="">Any</option>
            <option v-for="o in f.options || []" :key="o" :value="o">{{ o }}</option>
          </select>
          <input v-else-if="f.type === 'year' || f.type === 'number'" v-model.number="values[f.name]" type="number">
          <input v-else-if="f.type === 'date'" v-model="values[f.name]" type="date">
          <input v-else v-model="values[f.name]" type="text">
        </label>
      </div>
      <div v-if="canEdit" class="pc-actions">
        <template v-if="!editing">
          <button class="pc-btn" @click="startEdit">Edit</button>
        </template>
        <template v-else>
          <button class="pc-btn" @click="addPlacement">Add widget</button>
          <button class="pc-btn pc-primary" :disabled="saving" @click="save">{{ saving ? 'Saving…' : 'Save' }}</button>
          <button class="pc-btn" @click="cancelEdit">Cancel</button>
        </template>
      </div>
    </div>
    <div v-if="saveError" class="pc-error">{{ saveError }}</div>

    <div class="pc-stage">
      <div ref="viewport" class="pc-viewport" :style="{ height: stageHeight + 'px' }">
        <div class="pc-canvas" :style="canvasStyle" @pointerdown.self="select(null)">
          <div
            v-for="p in draft.placements"
            :key="p.id"
            class="pc-place"
            :class="{ 'pc-selected': editing && selectedId === p.id }"
            :style="placeStyle(p)"
            @pointerdown="editing && select(p.id)"
          >
            <div v-if="editing" class="pc-grip" @pointerdown.stop.prevent="startDrag($event, p, 'move')">{{ p.id }}</div>
            <WidgetHost
              :placement="p"
              :widget="widgets[p.widget]"
              :page-slug="slug"
              :session-id="sessionId"
              :filters="values"
              :width="p.layout.w"
              :height="p.layout.h"
            />
            <div v-if="editing" class="pc-resize" @pointerdown.stop.prevent="startDrag($event, p, 'resize')" />
          </div>
        </div>
      </div>

      <aside v-if="editing" class="pc-panel">
        <template v-if="selected">
          <div class="pc-panel-title">Placement</div>
          <label class="pc-field"><span>Widget</span>
            <select v-model="selected.widget" @change="onWidgetChange(selected)">
              <option v-for="w in widgetList" :key="w.slug" :value="w.slug">{{ w.name }}{{ w.stock ? '' : ' (custom)' }}</option>
            </select>
          </label>
          <div v-for="inp in contractInputs(selected)" :key="inp.name" class="pc-input">
            <div class="pc-input-name">Input <b>{{ inp.name }}</b> <span v-if="inp.description">— {{ inp.description }}</span></div>
            <select :value="bindingKey(selected, inp.name)" @change="bind(selected, inp.name, $event.target.value)">
              <option value="">Choose a source…</option>
              <option v-for="s in sourcesFor(inp)" :key="s.kind + ':' + s.id" :value="s.kind + ':' + s.id">{{ s.kind }} · {{ s.name }}</option>
            </select>
            <div v-if="inp.columns && inp.columns.length" class="pc-hint">needs {{ inp.columns.map((c) => c.name).join(', ') }}</div>
          </div>
          <label class="pc-field"><span>Config (JSON)</span>
            <textarea v-model="configText" rows="8" @blur="applyConfig" />
          </label>
          <div v-if="configError" class="pc-error">{{ configError }}</div>
          <label class="pc-field"><span>Intent</span><input v-model="selected.intent" type="text"></label>
          <div class="pc-row">
            <label class="pc-field pc-num"><span>x</span><input v-model.number="selected.layout.x" type="number"></label>
            <label class="pc-field pc-num"><span>y</span><input v-model.number="selected.layout.y" type="number"></label>
            <label class="pc-field pc-num"><span>w</span><input v-model.number="selected.layout.w" type="number"></label>
            <label class="pc-field pc-num"><span>h</span><input v-model.number="selected.layout.h" type="number"></label>
          </div>
          <button class="pc-btn pc-danger" @click="removePlacement(selected.id)">Remove</button>
        </template>
        <template v-else>
          <div class="pc-panel-title">Page</div>
          <label class="pc-field"><span>Canvas width</span><input v-model.number="draft.canvas.width" type="number" min="320" max="4000"></label>
          <label class="pc-field"><span>Filters (JSON)</span>
            <textarea v-model="filtersText" rows="8" @blur="applyFilters" />
          </label>
          <div v-if="filtersError" class="pc-error">{{ filtersError }}</div>
          <div class="pc-hint">Select a placement to edit it, or drag by its handle.</div>
        </template>
      </aside>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import WidgetHost from './WidgetHost.vue';

function clone(v) {
  return JSON.parse(JSON.stringify(v));
}

export default {
  name: 'PageCanvas',
  components: { WidgetHost },
  props: {
    slug: { type: String, default: '' },
    sessionId: { type: String, default: '' },
    spec: { type: Object, required: true },
    widgets: { type: Object, default: () => ({}) },
    canEdit: { type: Boolean, default: false },
  },
  emits: ['saved'],
  data() {
    return {
      draft: clone(this.spec),
      values: this.initialValues(this.spec),
      editing: false,
      selectedId: null,
      saving: false,
      saveError: '',
      viewportWidth: 0,
      widgetList: [],
      sources: [],
      configText: '',
      configError: '',
      filtersText: '',
      filtersError: '',
      drag: null,
    };
  },
  computed: {
    canvasWidth() {
      return Number((this.draft.canvas || {}).width) || 1200;
    },
    scale() {
      if (!this.viewportWidth) return 1;
      return Math.min(1, this.viewportWidth / this.canvasWidth);
    },
    canvasHeight() {
      const bottoms = (this.draft.placements || []).map((p) => p.layout.y + p.layout.h);
      return Math.max(240, ...bottoms) + 24;
    },
    stageHeight() {
      return Math.ceil(this.canvasHeight * this.scale);
    },
    canvasStyle() {
      return {
        width: this.canvasWidth + 'px',
        height: this.canvasHeight + 'px',
        transform: `scale(${this.scale})`,
        background: (this.draft.canvas || {}).background || 'transparent',
      };
    },
    selected() {
      return (this.draft.placements || []).find((p) => p.id === this.selectedId) || null;
    },
  },
  watch: {
    spec(next) {
      if (!this.editing) {
        this.draft = clone(next);
        this.values = this.initialValues(next);
      }
    },
    selectedId() {
      this.configText = this.selected ? JSON.stringify(this.selected.config || {}, null, 2) : '';
      this.configError = '';
    },
  },
  mounted() {
    this.measure();
    window.addEventListener('resize', this.measure);
    window.addEventListener('pointermove', this.onDrag);
    window.addEventListener('pointerup', this.endDrag);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.measure);
    window.removeEventListener('pointermove', this.onDrag);
    window.removeEventListener('pointerup', this.endDrag);
  },
  methods: {
    initialValues(spec) {
      const values = {};
      (spec.filters || []).forEach((f) => { values[f.name] = f.default ?? ''; });
      return values;
    },
    measure() {
      this.viewportWidth = this.$refs.viewport ? this.$refs.viewport.clientWidth : 0;
    },
    placeStyle(p) {
      return {
        left: p.layout.x + 'px', top: p.layout.y + 'px',
        width: p.layout.w + 'px', height: p.layout.h + 'px',
        zIndex: p.layout.z || 1,
      };
    },
    select(id) {
      this.selectedId = id;
    },
    async startEdit() {
      this.editing = true;
      this.draft = clone(this.spec);
      this.filtersText = JSON.stringify(this.draft.filters || [], null, 2);
      if (!this.widgetList.length) {
        const [{ data: w }, { data: s }] = await Promise.all([
          axios.get('/api/impact_analytics/widgets/'),
          axios.get('/api/impact_analytics/agent-pages/sources/'),
        ]);
        this.widgetList = w.widgets || [];
        this.sources = s.sources || [];
      }
    },
    cancelEdit() {
      this.editing = false;
      this.selectedId = null;
      this.saveError = '';
      this.draft = clone(this.spec);
    },
    widgetDef(slug) {
      return this.widgetList.find((w) => w.slug === slug) || this.widgets[slug] || null;
    },
    contractInputs(p) {
      const w = this.widgetDef(p.widget);
      return ((w && w.contract) || {}).inputs || [];
    },
    sourcesFor(inp) {
      const kinds = inp.kinds && inp.kinds.length ? inp.kinds : null;
      return this.sources.filter((s) => !kinds || kinds.includes(s.kind));
    },
    bindingKey(p, name) {
      const b = (p.inputs || {})[name];
      return b ? `${b.kind}:${b.id}` : '';
    },
    bind(p, name, key) {
      if (!p.inputs) p.inputs = {};
      if (!key) {
        delete p.inputs[name];
        return;
      }
      const [kind, id] = key.split(':');
      const source = this.sources.find((s) => s.kind === kind && String(s.id) === id);
      p.inputs[name] = { kind, id: Number(id), params: {}, label: source ? source.name : '' };
    },
    onWidgetChange(p) {
      const names = new Set(this.contractInputs(p).map((i) => i.name));
      Object.keys(p.inputs || {}).forEach((n) => { if (!names.has(n)) delete p.inputs[n]; });
    },
    applyConfig() {
      if (!this.selected) return;
      try {
        const parsed = JSON.parse(this.configText || '{}');
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('Config must be a JSON object.');
        this.selected.config = parsed;
        this.configError = '';
      } catch (e) {
        this.configError = e.message;
      }
    },
    applyFilters() {
      try {
        const parsed = JSON.parse(this.filtersText || '[]');
        if (!Array.isArray(parsed)) throw new Error('Filters must be a JSON list.');
        this.draft.filters = parsed;
        this.filtersError = '';
      } catch (e) {
        this.filtersError = e.message;
      }
    },
    addPlacement() {
      const base = 'placement';
      let n = (this.draft.placements || []).length + 1;
      while (this.draft.placements.some((p) => p.id === `${base}-${n}`)) n += 1;
      const first = this.widgetList.find((w) => w.slug === 'heading') || this.widgetList[0];
      const placement = {
        id: `${base}-${n}`, widget: first ? first.slug : 'heading', inputs: {},
        config: first && first.slug === 'heading' ? { text: 'New section', level: 2 } : {},
        layout: { x: 24, y: this.canvasHeight - 24, w: 480, h: 240 }, intent: '',
      };
      this.draft.placements.push(placement);
      this.selectedId = placement.id;
    },
    removePlacement(id) {
      this.draft.placements = this.draft.placements.filter((p) => p.id !== id);
      this.selectedId = null;
    },
    startDrag(event, p, mode) {
      this.selectedId = p.id;
      this.drag = { id: p.id, mode, startX: event.clientX, startY: event.clientY, layout: { ...p.layout } };
    },
    onDrag(event) {
      if (!this.drag) return;
      const p = this.draft.placements.find((x) => x.id === this.drag.id);
      if (!p) return;
      const dx = (event.clientX - this.drag.startX) / this.scale;
      const dy = (event.clientY - this.drag.startY) / this.scale;
      const snap = (v) => Math.max(0, Math.round(v / 8) * 8);
      if (this.drag.mode === 'move') {
        p.layout.x = snap(this.drag.layout.x + dx);
        p.layout.y = snap(this.drag.layout.y + dy);
      } else {
        p.layout.w = Math.max(80, snap(this.drag.layout.w + dx));
        p.layout.h = Math.max(48, snap(this.drag.layout.h + dy));
      }
    },
    endDrag() {
      this.drag = null;
    },
    async save() {
      this.applyConfig();
      this.applyFilters();
      if (this.configError || this.filtersError) return;
      this.saving = true;
      this.saveError = '';
      try {
        const { data } = await axios.put(`/api/impact_analytics/agent-pages/${this.slug}/spec/`, { spec: this.draft });
        this.editing = false;
        this.selectedId = null;
        this.$emit('saved', data);
      } catch (err) {
        this.saveError = err?.response?.data?.error || 'The page could not be saved.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.pc { display: flex; flex-direction: column; gap: 10px; }
.pc-bar { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.pc-filters { display: flex; gap: 10px; flex-wrap: wrap; }
.pc-filter { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #56534d; }
.pc-filter input, .pc-filter select { font-size: 12.5px; padding: 4px 6px; border: 1px solid #dad8d2; border-radius: 6px; background: #fff; }
.pc-actions { display: flex; gap: 6px; }
.pc-btn { font-size: 12.5px; padding: 5px 10px; border: 1px solid #dad8d2; border-radius: 6px; background: #fff; color: #1a1918; cursor: pointer; }
.pc-primary { background: #EF6C3B; border-color: #EF6C3B; color: #fff; }
.pc-danger { color: #C0392B; margin-top: 8px; }
.pc-error { font-size: 12px; color: #c23a28; }
.pc-stage { display: flex; gap: 12px; align-items: flex-start; }
.pc-viewport { flex: 1; min-width: 0; position: relative; overflow: hidden; }
.pc-canvas { position: absolute; left: 0; top: 0; transform-origin: 0 0; }
.pc-place { position: absolute; }
.pc-editing .pc-place { outline: 1px dashed #dad8d2; }
.pc-selected { outline: 2px solid #EF6C3B !important; }
.pc-grip { position: absolute; left: 0; top: -18px; height: 18px; line-height: 18px; padding: 0 6px; font-size: 10.5px; color: #fff; background: #EF6C3B; border-radius: 4px 4px 0 0; cursor: move; user-select: none; z-index: 2; }
.pc-resize { position: absolute; right: -4px; bottom: -4px; width: 12px; height: 12px; background: #EF6C3B; border-radius: 3px; cursor: nwse-resize; z-index: 2; }
.pc-panel { width: 300px; flex: none; background: #fff; border: 1px solid #e6e4df; border-radius: 8px; padding: 12px; display: flex; flex-direction: column; gap: 8px; font-size: 12.5px; }
.pc-panel-title { font-weight: 600; color: #1a1918; }
.pc-field { display: flex; flex-direction: column; gap: 3px; color: #56534d; }
.pc-field input, .pc-field select, .pc-field textarea, .pc-input select { font-size: 12.5px; padding: 5px 6px; border: 1px solid #dad8d2; border-radius: 6px; background: #fff; font-family: inherit; }
.pc-field textarea { font-family: 'IBM Plex Mono', ui-monospace, monospace; font-size: 11.5px; }
.pc-row { display: flex; gap: 6px; }
.pc-num { flex: 1; }
.pc-input { display: flex; flex-direction: column; gap: 3px; }
.pc-input-name { color: #56534d; }
.pc-hint { font-size: 11px; color: #8b877f; }
</style>
